import { DataModelChange, ShapeDataModel, MutationSource, STPlaceholderType, DataType, IShapeDataModel, IEventManager } from '@tencent/slide-sdk-model';
import { ISlideTitleManager, titleChangedCallback } from '../../../core/interfaces/slide-title-manager.interface';
import { IPlaceholderShapeDataModel } from '@tencent/slide-sdk-model/src/model/group-shape-data-model';
import { DefaultSlideTitleManager } from './slide-title-manager.online';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import { ILogger, SlideDataServices } from '../../../core/slide-data-services.core';
import { getTitleFromText } from '../../../utils/title-manager-utils';
import { IPresentationStoreService } from '../../../core/interfaces/presentation-service.interface';

const TITLE_CHANGE_EVENT = 'title-change-event';

export class SlideTitleManagerV3 extends DefaultSlideTitleManager implements ISlideTitleManager {
    private firstSlideTitleObserver: (change: DataModelChange) => void; // 标题变更监听函数实例
    private observedShapes = new Set<IShapeDataModel>();

    public constructor(
        clientVarsService: IClientVarsService,
        logger: ILogger,
        _,
        private presentationStoreService: IPresentationStoreService,
        sdkEvent: IEventManager,
    ) {
        super(clientVarsService, logger);
        // 接收转发 socket 事件。多一层转发的原因是 socket 事件无法自定义发送
        sdkEvent.socketEvents.onTitleChange(({ title }) => {
            this.events.emit(TITLE_CHANGE_EVENT, title);
        });
        // 标题已经被修改过的话就不添加监听了，节省资源
        if (this.isUserChangeTitle) {
            return;
        }
        this.addPresentationObserver(); // 添加 Presentation 监听（slideList 改变时修改第一页标题形状监听）
        this.resetFirstSlideObserver(); // 添加第一页标题形状的监听
    }

    // 添加标题实际变更后的回调
    public onTitleChanged(callback: titleChangedCallback) {
        super.onTitleChanged(callback);
        this.events.on(TITLE_CHANGE_EVENT, (title: string) => callback(title));
    }

    protected setDocVisible(): void {
        SlideDataServices.DocInfoManager.promise().then(docInfoManager => docInfoManager.setDocVisible());
    }

    /**
     * 主动触发标题更新
     */
    protected emitTitleChanged(title: string) {
        super.emitTitleChanged(title);
        this.events.emit(TITLE_CHANGE_EVENT, title);
    }

    private addPresentationObserver() {
        const observer = (change: DataModelChange) => this.observerPresentation(change);
        this.presentationStoreService.getPresentation().subscribe(observer);
    }

    /**
     * 监听presentation的变化，如果slide页有增减，需重新设置第一页占位符的监听
     * @param change presentation 变化集
     */
    private observerPresentation(change: DataModelChange) {
        // 只有编辑区的改动 或者 undoRedo 才会触发标题变更
        if ([MutationSource.Editor, MutationSource.UndoRedo].includes(change.source)) {
            // 如果文档标题被手动修改过，移除监听
            if (this.isUserChangeTitle) {
                this.presentationStoreService.getPresentation().unsubscribe(this.observerPresentation);
                return;
            }
            // 如果slide页有增减变更，需要重置新的slide页占位符的监听
            this.resetFirstSlideObserver();
        }
    }

    /**
     * 重新在第一页 slide 注册监听
     */
    private resetFirstSlideObserver() {
        const { slideList } = this.presentationStoreService.getPresentation();
        if (slideList && slideList.length > 0) {
            this.removeSlideObservers();
            const [firstSlide] = slideList;
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < firstSlide.shapeList.length; i++) {
                const element = firstSlide.shapeList[i];
                if (element.type === DataType.Shape && this.isTitlePh(element)) {
                    this.firstSlideTitleObserver = change => this.titlePhObserver(change, element as ShapeDataModel);
                    element.subscribe(this.firstSlideTitleObserver);
                    this.observedShapes.add(element);
                }
            }
        } else {
            this.removeSlideObservers();
        }
    }

    /**
     * slide 里的标题添加监听
     * @param change 变化集
     * @param sdkData 额外数据
     */
    private titlePhObserver(change: DataModelChange, sdkData: ShapeDataModel) {
        if (![MutationSource.Editor, MutationSource.UndoRedo].includes(change.source)) {
            return;
        }
        const paragraphListLength = sdkData.props.txBody?.p?.length;
        if (!paragraphListLength) {
            return;
        }
        if (this.isUserChangeTitle) {
            sdkData.unsubscribe(this.firstSlideTitleObserver);
            return;
        }
        const textStr = this.getTextStr(sdkData);
        const { isValid, newTitle } = getTitleFromText(textStr);
        if (!isValid) {
            return;
        }
        this.updateTitle(newTitle);
    }

    private removeSlideObservers() {
        this.observedShapes.forEach((shape) => {
            shape?.unsubscribe(this.firstSlideTitleObserver);
        });
        this.observedShapes.clear();
    }

    private isTitlePh(sdkData: IPlaceholderShapeDataModel) {
        const placeholderType = sdkData?.props?.nvPr?.ph?.type;
        return placeholderType === STPlaceholderType.title || placeholderType === STPlaceholderType.ctrTitle;
    }

    private getTextStr(sdkData: ShapeDataModel) {
        let textStr = '';
        sdkData.props.txBody?.p?.forEach((paragraph) => {
            if (paragraph.egTextRun) {
                paragraph.egTextRun.forEach((textRun) => {
                    if (textRun?.r?.t) {
                        textStr += textRun.r.t;
                    }
                });
            }
        });
        return textStr;
    }
}
